import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { buildCalendarPath } from './calendarRoutes';

async function findOrCreateDirectConversation(db, userA, userB) {
  const participants = [userA, userB].sort();
  const snap = await getDocs(
    query(collection(db, 'conversations'), where('participants', 'array-contains', userA), where('type', '==', 'direct'))
  );
  const existing = snap.docs.find((d) => {
    const p = d.data()?.participants || [];
    return p.length === 2 && p.includes(userB);
  });
  if (existing) return existing.id;

  const ref = await addDoc(collection(db, 'conversations'), {
    participants,
    type: 'direct',
    name: null,
    lastMessage: null,
    lastActivity: serverTimestamp(),
    createdAt: serverTimestamp(),
    createdBy: userA,
    unreadCounts: { [userA]: 0, [userB]: 0 },
    isActive: true,
  });
  return ref.id;
}

function originBase() {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return 'https://venuereportcard.com';
}

/**
 * Ping the tour owner / setlist moderator when someone proposes order or BPM.
 */
export async function notifySetlistProposal({
  db,
  senderId,
  senderName,
  recipientIds = [],
  tourName,
  dateKey,
  dateLabel,
}) {
  const from = String(senderId || '').trim();
  const unique = [...new Set((recipientIds || []).map((id) => String(id || '').trim()).filter(Boolean))]
    .filter((id) => id !== from);
  if (!db || !from || !unique.length) return { skipped: true };

  const path = buildCalendarPath('setlist', dateKey);
  const shareUrl = `${originBase()}${path}`;
  const label = dateLabel || dateKey || 'this day';
  const who = senderName || 'A tour member';
  const text = `${who} proposed a setlist change for ${label}. Open the setlist to accept it or set the order yourself.`;

  await Promise.all(unique.map(async (recipientUserId) => {
    const conversationId = await findOrCreateDirectConversation(db, from, recipientUserId);
    await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
      text,
      senderId: from,
      senderName: who,
      senderPhoto: '',
      timestamp: serverTimestamp(),
      type: 'setlist_proposal',
      shareUrl,
      calendarPath: path,
      tourName: String(tourName || 'Tour').trim(),
      dateKey: String(dateKey || '').trim(),
      dateLabel: String(label).trim(),
      readBy: { [from]: serverTimestamp() },
      edited: false,
    });
    const convSnap = await getDoc(doc(db, 'conversations', conversationId));
    const prevUnread = convSnap.data()?.unreadCounts?.[recipientUserId] || 0;
    await updateDoc(doc(db, 'conversations', conversationId), {
      lastMessage: {
        text: `Setlist proposal · ${tourName || 'Tour'}`,
        senderId: from,
        timestamp: serverTimestamp(),
        type: 'setlist_proposal',
      },
      lastActivity: serverTimestamp(),
      [`unreadCounts.${recipientUserId}`]: prevUnread + 1,
    });
  }));
  return { sent: unique.length, shareUrl };
}
